(() => {
  const root = document.querySelector('[data-custom-collection]');
  if (!root) return;

  const routesRoot = window.Shopify?.routes?.root || '/';
  const indexSection = root.dataset.indexSection || 'collection-index-data';
  const pageSize = Math.max(1, parseInt(root.dataset.pageSize || '24', 10));
  const currency = root.dataset.currency || 'AUD';

  const treeEl = root.querySelector('[data-collection-subcategory-tree]');
  const filtersEl = root.querySelector('[data-collection-filters]');
  const filterStatus = root.querySelector('[data-collection-filter-status]');
  const subSection = root.querySelector('[data-collection-subcategory-section]');
  const subList = root.querySelector('[data-collection-subcategory-list]');
  const clearAllButtons = root.querySelectorAll('[data-collection-clear-all]');
  const priceMinInput = root.querySelector('[data-collection-price-min]');
  const priceMaxInput = root.querySelector('[data-collection-price-max]');
  const priceQuick = root.querySelector('[data-collection-price-quick]');
  const priceResetBtn = root.querySelector('[data-collection-price-reset]');
  const resultsTarget = root.querySelector('[data-collection-results]');
  const resultCount = root.querySelector('[data-collection-result-count]');
  const loadingEl = root.querySelector('[data-collection-index-loading]');
  const paginationEl = root.querySelector('[data-collection-client-pagination]');
  const sortSelect = root.querySelector('[data-collection-sort]');
  const sortSwitch = root.querySelector('[data-collection-sort-switch]');
  const viewSwitch = root.querySelector('[data-collection-view-switch]');
  const sidebar = root.querySelector('.collection-sidebar');

  if (!treeEl || !resultsTarget) return;

  let tree = { category: {}, children: [] };
  try {
    tree = JSON.parse(treeEl.textContent || '{"category":{},"children":[]}');
  } catch (error) {
    console.error('Could not parse collection subcategory tree.', error);
  }

  let loadedIndexKey = '';

  const state = {
    products: [],
    subcategories: new Set(),
    min: null,
    max: null,
    page: 1,
    ready: false
  };

  function normalizeHandle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^\/+|\/+$/g, '');
  }

  function collectionHandleFromUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      const parts = parsed.pathname.split('/').filter(Boolean);
      const index = parts.indexOf('collections');
      return index >= 0 && parts[index + 1] ? normalizeHandle(parts[index + 1]) : '';
    } catch (error) {
      return '';
    }
  }

  tree.children = (tree.children || [])
    .map((child) => ({
      title: child.title || '',
      url: child.url || '#',
      handle: normalizeHandle(child.handle) || collectionHandleFromUrl(child.url)
    }))
    .filter((child) => child.handle);

  function money(cents) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format((Number(cents) || 0) / 100);
  }

  function compactMoney(dollars) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(Number(dollars) || 0);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function productCollectionSet(product) {
    if (!product.__collectionSet) {
      product.__collectionSet = new Set(
        (product.collections || []).map((collection) => normalizeHandle(collection.handle))
      );
    }
    return product.__collectionSet;
  }

  function matchesChild(product, handle) {
    return productCollectionSet(product).has(normalizeHandle(handle));
  }

  function matchesSelectedSubcategories(product) {
    if (!state.subcategories.size) return true;

    for (const handle of state.subcategories) {
      if (matchesChild(product, handle)) return true;
    }

    return false;
  }

  function matchesPrice(product) {
    const productMin = Number(product.priceMin) || 0;
    const productMax = Number(product.priceMax) || productMin;
    const minCents = state.min == null ? null : Math.round(state.min * 100);
    const maxCents = state.max == null ? null : Math.round(state.max * 100);

    if (minCents != null && productMax < minCents) return false;
    if (maxCents != null && productMin > maxCents) return false;

    return true;
  }

  function getFilteredProducts() {
    return state.products.filter(
      (product) => matchesSelectedSubcategories(product) && matchesPrice(product)
    );
  }

  function subcategoryCount(child) {
    return state.products.filter(
      (product) => matchesPrice(product) && matchesChild(product, child.handle)
    ).length;
  }

  function priceContextProducts() {
    return state.products.filter((product) => matchesSelectedSubcategories(product));
  }

  function overallPriceBounds(products = state.products) {
    if (!products.length) return { min: 0, max: 0 };

    let min = Infinity;
    let max = 0;

    products.forEach((product) => {
      min = Math.min(min, Number(product.priceMin) || 0);
      max = Math.max(max, Number(product.priceMax) || Number(product.priceMin) || 0);
    });

    return {
      min: Number.isFinite(min) ? min : 0,
      max
    };
  }

  function setLoading(on) {
    if (loadingEl) loadingEl.hidden = !on;
    filtersEl?.classList.toggle('is-updating', on);
    resultsTarget?.classList.toggle('is-updating', on);
  }

  function currentIndexCacheKey() {
    const url = new URL(window.location.href);
    const sort = url.searchParams.get('sort_by') || '';
    return `customCollectionIndex:v1:${url.pathname}::${sort}`;
  }

  function indexUrl(page) {
    const url = new URL(window.location.href);

    ['section_id', 'sub', 'cmin', 'cmax', 'cpage', 'page'].forEach((key) => {
      url.searchParams.delete(key);
    });

    url.searchParams.set('section_id', indexSection);
    url.searchParams.set('page', String(page));

    return url.toString();
  }

  async function fetchBatch(page) {
    const response = await fetch(indexUrl(page), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    });

    if (!response.ok) {
      throw new Error(`Collection index request failed (${response.status})`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('[data-collection-index-batch]');

    if (!script) {
      throw new Error('Collection index data section returned no JSON payload.');
    }

    return JSON.parse(script.textContent || '{}');
  }

  async function runPool(items, concurrency, worker) {
    const queue = [...items];
    const runners = Array.from(
      { length: Math.min(concurrency, queue.length) },
      async () => {
        while (queue.length) {
          const item = queue.shift();
          await worker(item);
        }
      }
    );

    await Promise.all(runners);
  }

  async function loadIndex() {
    const key = currentIndexCacheKey();

    try {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed.products)) return parsed.products;
      }
    } catch (error) {
      // Cache is optional.
    }

    const first = await fetchBatch(1);
    const products = [...(first.products || [])];
    const totalPages = Math.max(1, Number(first.pages) || 1);

    if (totalPages > 1) {
      const pages = [];
      for (let page = 2; page <= totalPages; page += 1) pages.push(page);

      await runPool(pages, 4, async (page) => {
        const batch = await fetchBatch(page);
        products.push(...(batch.products || []));
      });
    }

    const seen = new Set();
    const deduped = [];

    products.forEach((product) => {
      const keyValue = product.id || product.url;
      if (!keyValue || seen.has(keyValue)) return;
      seen.add(keyValue);
      deduped.push(product);
    });

    try {
      sessionStorage.setItem(key, JSON.stringify({ products: deduped }));
    } catch (error) {
      // Large collections can exceed sessionStorage. Continue without cache.
    }

    return deduped;
  }

  function readStateFromUrl() {
    const url = new URL(window.location.href);

    state.subcategories = new Set(
      (url.searchParams.get('sub') || '')
        .split(',')
        .map(normalizeHandle)
        .filter(Boolean)
    );

    const min = parseFloat(url.searchParams.get('cmin') || '');
    const max = parseFloat(url.searchParams.get('cmax') || '');
    const page = parseInt(url.searchParams.get('cpage') || '1', 10);

    state.min = Number.isFinite(min) ? Math.max(0, min) : null;
    state.max = Number.isFinite(max) ? Math.max(0, max) : null;
    state.page = Number.isFinite(page) && page > 0 ? page : 1;

    if (priceMinInput) priceMinInput.value = state.min ?? '';
    if (priceMaxInput) priceMaxInput.value = state.max ?? '';
  }

  function writeStateToUrl({ replace = false } = {}) {
    const url = new URL(window.location.href);

    if (state.subcategories.size) {
      url.searchParams.set('sub', [...state.subcategories].join(','));
    } else {
      url.searchParams.delete('sub');
    }

    if (state.min != null) url.searchParams.set('cmin', String(state.min));
    else url.searchParams.delete('cmin');

    if (state.max != null) url.searchParams.set('cmax', String(state.max));
    else url.searchParams.delete('cmax');

    if (state.page > 1) url.searchParams.set('cpage', String(state.page));
    else url.searchParams.delete('cpage');

    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    history[replace ? 'replaceState' : 'pushState']({}, '', url);
  }

  function clearInvalidSelections() {
    const allowed = new Set(tree.children.map((child) => child.handle));
    state.subcategories = new Set(
      [...state.subcategories].filter((handle) => allowed.has(handle))
    );
  }

  function renderSubcategories() {
    if (!subSection || !subList) return;

    if (!tree.children.length) {
      subSection.hidden = true;
      subList.innerHTML = '';
      return;
    }

    const rows = tree.children
      .map((child) => ({ child, count: subcategoryCount(child) }))
      .filter((entry) => entry.count > 0 || state.subcategories.has(entry.child.handle))
      .sort((a, b) => b.count - a.count || a.child.title.localeCompare(b.child.title));

    if (!rows.length) {
      subSection.hidden = true;
      subList.innerHTML = '';
      return;
    }

    subSection.hidden = false;
    subList.innerHTML = rows
      .map(({ child, count }) => {
        const active = state.subcategories.has(child.handle);

        return `
          <li class="facet__value">
            <label>
              <input
                type="checkbox"
                data-collection-subcategory="${escapeHtml(child.handle)}"
                ${active ? 'checked' : ''}
              >
              <span>${escapeHtml(child.title)}</span>
              <span class="facet__value-count">(${count})</span>
            </label>
          </li>
        `;
      })
      .join('');
  }

  function nicePriceStep(maxDollars) {
    if (!Number.isFinite(maxDollars) || maxDollars <= 0) return 100;

    const raw = maxDollars / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
    const normalized = raw / magnitude;

    let nice;
    if (normalized <= 1) nice = 1;
    else if (normalized <= 2) nice = 2;
    else if (normalized <= 2.5) nice = 2.5;
    else if (normalized <= 5) nice = 5;
    else nice = 10;

    return Math.max(1, nice * magnitude);
  }

  function productMatchesPriceBand(product, minDollars, maxDollars) {
    const productMin = (Number(product.priceMin) || 0) / 100;
    const productMax = (Number(product.priceMax) || Number(product.priceMin) || 0) / 100;

    if (minDollars != null && productMax < minDollars) return false;
    if (maxDollars != null && productMin > maxDollars) return false;
    return true;
  }

  function buildQuickPriceBands() {
    const products = priceContextProducts();
    if (!products.length) return [];

    let maximum = 0;
    products.forEach((product) => {
      maximum = Math.max(
        maximum,
        (Number(product.priceMax) || Number(product.priceMin) || 0) / 100
      );
    });

    if (maximum <= 0) return [];

    const step = nicePriceStep(maximum);
    const bands = [];
    const maxRegularBands = 5;

    for (let index = 0; index < maxRegularBands; index += 1) {
      const min = index * step;
      const max = (index + 1) * step;
      if (min >= maximum) break;

      const filterMax = Math.max(min, max - 0.01);
      const count = products.filter((product) =>
        productMatchesPriceBand(product, min, filterMax)
      ).length;

      if (!count) continue;

      bands.push({
        min,
        max: filterMax,
        count,
        label:
          index === 0
            ? `Under ${compactMoney(max)}`
            : `${compactMoney(min)} – ${compactMoney(max)}`
      });
    }

    const overflowMin = maxRegularBands * step;
    if (maximum >= overflowMin) {
      const count = products.filter((product) =>
        productMatchesPriceBand(product, overflowMin, null)
      ).length;

      if (count) {
        bands.push({
          min: overflowMin,
          max: null,
          count,
          label: `${compactMoney(overflowMin)}+`
        });
      }
    }

    return bands;
  }

  function isSamePriceBand(band) {
    const maxMatches = band.max == null ? state.max == null : state.max === band.max;
    return state.min === band.min && maxMatches;
  }

  function renderQuickPrice() {
    if (!priceQuick) return;

    const bands = buildQuickPriceBands();
    if (!bands.length) {
      priceQuick.innerHTML = '';
      return;
    }

    priceQuick.innerHTML = `
      <div class="price-quick__label">Quick price</div>
      <div class="price-quick__grid">
        ${bands
          .map((band) => {
            const active = isSamePriceBand(band);
            return `
              <button
                type="button"
                class="price-quick__btn${active ? ' is-active' : ''}"
                data-collection-price-band
                data-min="${band.min}"
                data-max="${band.max == null ? '' : band.max}"
                aria-pressed="${active ? 'true' : 'false'}"
              >
                <span class="facet__value-label">${escapeHtml(band.label)}</span>
                <span class="facet__value-count">(${band.count})</span>
              </button>
            `;
          })
          .join('')}
      </div>
    `;
  }

  function renderPriceState() {
    const bounds = overallPriceBounds(priceContextProducts());

    if (priceMinInput) {
      priceMinInput.placeholder = String(Math.floor(bounds.min / 100));
    }

    if (priceMaxInput) {
      priceMaxInput.placeholder = String(Math.ceil(bounds.max / 100));
    }

    if (priceResetBtn) {
      priceResetBtn.hidden = state.min == null && state.max == null;
    }

    renderQuickPrice();
  }

  function renderFilterHeader() {
    const activeCount =
      state.subcategories.size + (state.min != null || state.max != null ? 1 : 0);

    clearAllButtons.forEach((button) => {
      button.hidden = activeCount === 0;
    });

    if (filterStatus) {
      filterStatus.hidden = state.ready;
      filterStatus.textContent = state.ready ? '' : 'Loading products…';
    }
  }

  function productUrlWithVariant(url, variantId) {
    if (!url || !variantId) return url || '#';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('variant', String(variantId));
      return parsed.pathname + parsed.search + parsed.hash;
    } catch (error) {
      return url;
    }
  }

  function cardHtml(product) {
    const price =
      Number(product.priceMin) !== Number(product.priceMax)
        ? `From ${money(product.priceMin)}`
        : money(product.priceMin);

    const image = product.image
      ? `
        <img
          class="product-card__image"
          src="${escapeHtml(product.image)}"
          alt="${escapeHtml(product.imageAlt || product.title)}"
          loading="lazy"
          width="800"
          height="800"
        >
      `
      : '';

    const disabled = product.available ? '' : ' disabled';
    const atcText = product.available ? 'Add to cart' : 'Sold out';
    const sku = product.sku
      ? `<div class="product-card__sku">SKU: ${escapeHtml(product.sku)}</div>`
      : '';
    const url = productUrlWithVariant(product.url, product.variantId);

    return `
      <article class="product-card" data-available="${product.available ? 'true' : 'false'}">
        <a class="product-card__link" href="${escapeHtml(url)}">
          <div class="product-card__media">${image}</div>

          <div class="product-card__body">
            <h3 class="product-card__title">${escapeHtml(product.title)}</h3>
            ${sku}
            <div class="product-card__price">${escapeHtml(price)}</div>
          </div>
        </a>

        <div class="product-card__actions">
          <button
            class="product-card__btn product-card__btn--atc"
            type="button"
            data-collection-add="${escapeHtml(product.variantId)}"
            ${disabled}
          >
            ${atcText}
          </button>

          <button
            class="product-card__btn product-card__btn--bin"
            type="button"
            data-collection-buy="${escapeHtml(product.variantId)}"
            ${disabled}
          >
            Buy it now
          </button>
        </div>
      </article>
    `;
  }

  function renderPagination(totalItems) {
    if (!paginationEl) return;

    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (state.page > totalPages) state.page = totalPages;

    if (totalPages <= 1) {
      paginationEl.hidden = true;
      paginationEl.innerHTML = '';
      return;
    }

    const parts = [];
    const addPage = (page) => {
      parts.push(
        page === state.page
          ? `<span class="pagination__link is-current" aria-current="page">${page}</span>`
          : `<button type="button" class="pagination__link" data-collection-client-page="${page}">${page}</button>`
      );
    };

    const start = Math.max(1, state.page - 2);
    const end = Math.min(totalPages, state.page + 2);

    if (start > 1) {
      addPage(1);
      if (start > 2) parts.push('<span class="pagination__ellipsis">…</span>');
    }

    for (let page = start; page <= end; page += 1) addPage(page);

    if (end < totalPages) {
      if (end < totalPages - 1) parts.push('<span class="pagination__ellipsis">…</span>');
      addPage(totalPages);
    }

    paginationEl.innerHTML = `
      <button
        type="button"
        class="pagination__btn"
        data-collection-client-page="${Math.max(1, state.page - 1)}"
        ${state.page === 1 ? 'disabled aria-disabled="true"' : ''}
      >
        ← Prev
      </button>

      <div class="custom-collection-pagination__pages">${parts.join('')}</div>

      <button
        type="button"
        class="pagination__btn"
        data-collection-client-page="${Math.min(totalPages, state.page + 1)}"
        ${state.page === totalPages ? 'disabled aria-disabled="true"' : ''}
      >
        Next →
      </button>
    `;

    paginationEl.hidden = false;
  }

  function renderProducts() {
    const filtered = getFilteredProducts();
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (state.page > totalPages) state.page = totalPages;

    const start = (state.page - 1) * pageSize;
    const pageProducts = filtered.slice(start, start + pageSize);

    if (resultCount) {
      resultCount.textContent = `${filtered.length} product${filtered.length === 1 ? '' : 's'}`;
    }

    if (!pageProducts.length) {
      resultsTarget.innerHTML = `
        <div class="custom-collection__empty">
          <h2>No products match these filters</h2>
          <p>Try another subcategory or widen the price range.</p>
        </div>
      `;
    } else {
      resultsTarget.innerHTML = `
        <div class="product-grid">
          ${pageProducts.map(cardHtml).join('')}
        </div>
      `;
      applySavedView();
      scheduleUniformMediaHeight();
    }

    renderPagination(filtered.length);
  }

  function renderAll({ scroll = false } = {}) {
    clearInvalidSelections();
    renderSubcategories();
    renderPriceState();
    renderFilterHeader();
    renderProducts();

    if (scroll) {
      root.querySelector('.collection-main')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  function toggleSubcategory(handle) {
    handle = normalizeHandle(handle);

    if (state.subcategories.has(handle)) state.subcategories.delete(handle);
    else state.subcategories.add(handle);

    state.page = 1;
    writeStateToUrl();
    renderAll();
  }

  function applyTypedPrice() {
    const min = parseFloat(priceMinInput?.value || '');
    const max = parseFloat(priceMaxInput?.value || '');

    state.min = Number.isFinite(min) ? Math.max(0, min) : null;
    state.max = Number.isFinite(max) ? Math.max(0, max) : null;

    if (state.min != null && state.max != null && state.min > state.max) {
      [state.min, state.max] = [state.max, state.min];
      if (priceMinInput) priceMinInput.value = state.min;
      if (priceMaxInput) priceMaxInput.value = state.max;
    }

    state.page = 1;
    writeStateToUrl();
    renderAll();
  }

  let priceTimer = null;
  function scheduleTypedPrice() {
    window.clearTimeout(priceTimer);
    priceTimer = window.setTimeout(applyTypedPrice, 350);
  }

  async function addVariant(variantId, { buyNow = false, button = null } = {}) {
    if (!variantId) return;

    const originalText = button?.textContent || '';
    if (button) {
      button.disabled = true;
      button.textContent = buyNow ? 'Opening checkout…' : 'Adding…';
    }

    try {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      if (!response.ok) throw new Error('Could not add product to cart.');
      const added = await response.json();

      if (buyNow) {
        window.location.assign(`${routesRoot}checkout`);
        return;
      }

      window.dispatchEvent(new CustomEvent('cart:added', {
        detail: { item: added, quantity: 1 }
      }));

      window.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { open: true, source: 'collection-page', item: added, quantity: 1 }
      }));

      if (button) button.textContent = 'Added';
      setTimeout(() => {
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      }, 900);
    } catch (error) {
      console.error(error);
      if (button) {
        button.disabled = false;
        button.textContent = 'Try again';
        setTimeout(() => {
          button.textContent = originalText;
        }, 1400);
      }
    }
  }

  function clearAll() {
    state.subcategories.clear();
    state.min = null;
    state.max = null;
    state.page = 1;

    if (priceMinInput) priceMinInput.value = '';
    if (priceMaxInput) priceMaxInput.value = '';

    writeStateToUrl();
    renderAll();
  }

  function openMobileFilters() {
    document.body.classList.add('is-filters-open');
    root.querySelector('[data-filters-open]')?.setAttribute('aria-expanded', 'true');
    sidebar?.setAttribute('aria-hidden', 'false');
  }

  function closeMobileFilters() {
    document.body.classList.remove('is-filters-open');
    root.querySelector('[data-filters-open]')?.setAttribute('aria-expanded', 'false');
    if (window.matchMedia('(max-width: 990px)').matches) {
      sidebar?.setAttribute('aria-hidden', 'true');
    }
  }

  function getSavedView() {
    const saved = localStorage.getItem('collectionView');
    return saved === 'list' || saved === 'grid' ? saved : 'grid';
  }

  const VIEW_ICONS = {
    list:
      '<svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 6h12M4 10h12M4 14h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M2 6h0M2 10h0M2 14h0" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>',
    grid:
      '<svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true"><rect x="3" y="3" width="5.5" height="5.5" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.6"/><rect x="11.5" y="3" width="5.5" height="5.5" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.6"/><rect x="3" y="11.5" width="5.5" height="5.5" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.6"/><rect x="11.5" y="11.5" width="5.5" height="5.5" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>'
  };

  function applyView(view) {
    root.querySelector('.product-grid')?.classList.toggle('is-list', view === 'list');

    if (viewSwitch) {
      viewSwitch.querySelectorAll('[data-collection-view]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.collectionView === view);
      });

      const icon = viewSwitch.querySelector('[data-collection-view-trigger-icon]');
      const trigger = viewSwitch.querySelector('[data-collection-view-trigger]');
      if (icon) icon.innerHTML = VIEW_ICONS[view] || VIEW_ICONS.grid;
      trigger?.setAttribute('aria-label', view === 'list' ? 'List view' : 'Gallery view');
    }

    localStorage.setItem('collectionView', view);
    scheduleUniformMediaHeight();
  }

  function applySavedView() {
    applyView(getSavedView());
  }

  function closeToolbarMenus() {
    if (sortSwitch) {
      const menu = sortSwitch.querySelector('[data-collection-sort-menu]');
      const trigger = sortSwitch.querySelector('[data-collection-sort-trigger]');
      if (menu) menu.hidden = true;
      trigger?.setAttribute('aria-expanded', 'false');
    }

    if (viewSwitch) {
      const menu = viewSwitch.querySelector('[data-collection-view-menu]');
      const trigger = viewSwitch.querySelector('[data-collection-view-trigger]');
      if (menu) menu.hidden = true;
      trigger?.setAttribute('aria-expanded', 'false');
    }
  }

  function syncSortMenu() {
    if (!sortSwitch || !sortSelect) return;
    sortSwitch.querySelectorAll('[data-collection-sort-value]').forEach((button) => {
      button.classList.toggle(
        'is-active',
        button.dataset.collectionSortValue === sortSelect.value
      );
    });
  }

  async function changeSort(value) {
    if (!value) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    url.searchParams.delete('cpage');
    history.pushState({}, '', url);

    state.page = 1;
    setLoading(true);

    try {
      state.products = await loadIndex();
      loadedIndexKey = currentIndexCacheKey();
      state.ready = true;
      renderAll();
    } catch (error) {
      console.error('Collection sort/index refresh failed.', error);
      window.location.assign(url.toString());
    } finally {
      setLoading(false);
    }
  }

  let mediaTimer = null;
  function setUniformCardMediaHeight() {
    const medias = root.querySelectorAll('.product-card__media');
    const imgs = root.querySelectorAll('.product-card__image');
    if (!medias.length || !imgs.length) return;

    const mediaWidth = medias[0].getBoundingClientRect().width;
    if (!mediaWidth) return;

    let minNeeded = Infinity;

    imgs.forEach((img) => {
      const nw = img.naturalWidth || 0;
      const nh = img.naturalHeight || 0;
      if (!nw || !nh) return;

      const needed = mediaWidth * (nh / nw);
      if (needed > 0 && needed < minNeeded) minNeeded = needed;
    });

    if (!Number.isFinite(minNeeded)) return;

    const finalH = Math.max(180, Math.min(360, Math.round(minNeeded)));
    document.documentElement.style.setProperty('--card-media-height', `${finalH}px`);
  }

  function scheduleUniformMediaHeight() {
    window.clearTimeout(mediaTimer);
    mediaTimer = window.setTimeout(setUniformCardMediaHeight, 80);
  }

  root.addEventListener('change', (event) => {
    const checkbox = event.target.closest('[data-collection-subcategory]');
    if (checkbox) {
      toggleSubcategory(checkbox.dataset.collectionSubcategory);
      return;
    }

    if (event.target.matches('[data-collection-price-min], [data-collection-price-max]')) {
      scheduleTypedPrice();
    }
  });

  root.addEventListener('input', (event) => {
    if (event.target.matches('[data-collection-price-min], [data-collection-price-max]')) {
      scheduleTypedPrice();
    }
  });

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-filters-open]')) {
      event.preventDefault();
      openMobileFilters();
      return;
    }

    if (event.target.closest('[data-filters-close]')) {
      event.preventDefault();
      closeMobileFilters();
      return;
    }

    const clearButton = event.target.closest('[data-collection-clear-all]');
    if (clearButton) {
      event.preventDefault();
      clearAll();
      return;
    }

    const resetPrice = event.target.closest('[data-collection-price-reset]');
    if (resetPrice) {
      state.min = null;
      state.max = null;
      state.page = 1;
      if (priceMinInput) priceMinInput.value = '';
      if (priceMaxInput) priceMaxInput.value = '';
      writeStateToUrl();
      renderAll();
      return;
    }

    const priceBand = event.target.closest('[data-collection-price-band]');
    if (priceBand) {
      const min = parseFloat(priceBand.dataset.min || '');
      const max = parseFloat(priceBand.dataset.max || '');
      const band = {
        min: Number.isFinite(min) ? min : 0,
        max: Number.isFinite(max) ? max : null
      };

      if (isSamePriceBand(band)) {
        state.min = null;
        state.max = null;
      } else {
        state.min = band.min;
        state.max = band.max;
      }

      state.page = 1;
      if (priceMinInput) priceMinInput.value = state.min ?? '';
      if (priceMaxInput) priceMaxInput.value = state.max ?? '';
      writeStateToUrl();
      renderAll();
      return;
    }

    const pageButton = event.target.closest('[data-collection-client-page]');
    if (pageButton && !pageButton.disabled) {
      state.page = Math.max(1, parseInt(pageButton.dataset.collectionClientPage || '1', 10));
      writeStateToUrl();
      renderAll({ scroll: true });
      return;
    }

    const addButton = event.target.closest('[data-collection-add]');
    if (addButton) {
      addVariant(addButton.dataset.collectionAdd, { button: addButton });
      return;
    }

    const buyButton = event.target.closest('[data-collection-buy]');
    if (buyButton) {
      addVariant(buyButton.dataset.collectionBuy, { buyNow: true, button: buyButton });
      return;
    }

    const buyNowFallback = event.target.closest('[data-buy-now]');
    if (buyNowFallback) {
      event.preventDefault();
      addVariant(buyNowFallback.dataset.variantId, { buyNow: true, button: buyNowFallback });
      return;
    }

    const sortTrigger = event.target.closest('[data-collection-sort-trigger]');
    if (sortTrigger && sortSwitch) {
      event.preventDefault();
      const menu = sortSwitch.querySelector('[data-collection-sort-menu]');
      const open = !!menu?.hidden;
      closeToolbarMenus();
      if (menu) menu.hidden = !open;
      sortTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    const sortOption = event.target.closest('[data-collection-sort-value]');
    if (sortOption) {
      const value = sortOption.dataset.collectionSortValue;
      if (sortSelect) sortSelect.value = value;
      syncSortMenu();
      closeToolbarMenus();
      changeSort(value);
      return;
    }

    const viewTrigger = event.target.closest('[data-collection-view-trigger]');
    if (viewTrigger && viewSwitch) {
      event.preventDefault();
      const menu = viewSwitch.querySelector('[data-collection-view-menu]');
      const open = !!menu?.hidden;
      closeToolbarMenus();
      if (menu) menu.hidden = !open;
      viewTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    const viewOption = event.target.closest('[data-collection-view]');
    if (viewOption) {
      applyView(viewOption.dataset.collectionView || 'grid');
      closeToolbarMenus();
    }
  });

  sortSelect?.addEventListener('change', () => changeSort(sortSelect.value));

  document.addEventListener('click', (event) => {
    const insideSort = sortSwitch?.contains(event.target);
    const insideView = viewSwitch?.contains(event.target);
    if (!insideSort && !insideView) closeToolbarMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeToolbarMenus();
    closeMobileFilters();
  });

  window.addEventListener('popstate', async () => {
    if (!state.ready) return;

    readStateFromUrl();

    const nextKey = currentIndexCacheKey();
    if (nextKey !== loadedIndexKey) {
      setLoading(true);
      try {
        state.products = await loadIndex();
        loadedIndexKey = nextKey;
      } catch (error) {
        console.error('Collection history refresh failed.', error);
      } finally {
        setLoading(false);
      }
    }

    renderAll();
  });

  window.addEventListener('resize', scheduleUniformMediaHeight);
  document.addEventListener(
    'load',
    (event) => {
      if (event.target?.classList?.contains('product-card__image')) {
        scheduleUniformMediaHeight();
      }
    },
    true
  );

  async function init() {
    readStateFromUrl();
    applySavedView();
    syncSortMenu();

    if (window.matchMedia('(max-width: 990px)').matches) {
      sidebar?.setAttribute('aria-hidden', 'true');
    }

    setLoading(true);

    try {
      state.products = await loadIndex();
      loadedIndexKey = currentIndexCacheKey();
      state.ready = true;
      renderAll();
    } catch (error) {
      console.error('Custom collection index failed.', error);

      if (filterStatus) filterStatus.textContent = 'Filters unavailable';
      if (subSection) subSection.hidden = true;

      // Keep the server-rendered collection grid usable as a fallback.
      applySavedView();
      scheduleUniformMediaHeight();
    } finally {
      setLoading(false);
    }
  }

  init();
})();
